import { useState, useEffect, useMemo } from 'react';
import { useAuthStore } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import { useDepos } from '../lib/useDepos';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { toast } from 'sonner';
import api from '../lib/api';
import { turkishIncludes } from '../lib/turkishSearch';
import {
  Loader2,
  ChevronLeft,
  ChevronRight,
  Users,
  RefreshCw,
  Briefcase,
  Coffee,
  Heart,
  Clock,
  CheckCircle,
  XCircle,
  Calendar,
  Eye,
  Building2,
  Baby,
  HelpCircle,
  CalendarDays,
  Filter,
  Search,
  X
} from 'lucide-react';
import { cn } from '../lib/utils';

// Status configuration - will be translated dynamically
const getStatusConfig = (t) => ({
  WORKING: { 
    label: t('employeeTracking.status.working') || 'Çalıştı',
    color: 'bg-emerald-500',
    textColor: 'text-white',
    code: 'Ç',
    icon: CheckCircle
  },
  HALF_DAY: { 
    label: t('employeeTracking.status.halfDay') || 'Yarım Gün',
    color: 'bg-amber-400',
    textColor: 'text-white',
    code: 'Y',
    icon: Clock
  },
  SICK: { 
    label: t('employeeTracking.status.sick') || 'Hasta',
    color: 'bg-red-500',
    textColor: 'text-white',
    code: 'H',
    icon: Heart
  },
  DOP: { 
    label: t('employeeTracking.status.dop') || 'DOP',
    color: 'bg-blue-500',
    textColor: 'text-white',
    code: 'D',
    icon: Coffee
  },
  ON_LEAVE: { 
    label: t('employeeTracking.status.onLeave') || 'İzinli',
    color: 'bg-orange-500',
    textColor: 'text-white',
    code: 'İ',
    icon: Briefcase
  },
  FEESTDAG: { 
    label: t('employeeTracking.status.holiday') || 'Tatil Günü',
    color: 'bg-purple-500',
    textColor: 'text-white',
    code: 'T',
    icon: Calendar
  },
  HALF_SICK: {
    label: t('employeeTracking.status.halfSick') || 'Yarım Hasta',
    color: 'bg-rose-400',
    textColor: 'text-white',
    code: 'HÇ',
    icon: Heart
  },
  ZATERDAG: {
    label: t('employeeTracking.status.saturday') || 'Cumartesi',
    color: 'bg-slate-500',
    textColor: 'text-white',
    code: 'C',
    icon: CalendarDays
  },
  PATERNITY: {
    label: t('employeeTracking.status.paternity') || 'Babalık İzni',
    color: 'bg-cyan-500',
    textColor: 'text-white',
    code: 'B',
    icon: Baby
  },
  NONE: {
    label: t('employeeTracking.status.unknown') || 'Belirsiz',
    color: 'bg-gray-400',
    textColor: 'text-white',
    code: '?',
    icon: HelpCircle
  }
});

const getMonthOptions = (t) => [
  { value: 1, label: t('employeeTracking.months.january') || 'Ocak' },
  { value: 2, label: t('employeeTracking.months.february') || 'Şubat' },
  { value: 3, label: t('employeeTracking.months.march') || 'Mart' },
  { value: 4, label: t('employeeTracking.months.april') || 'Nisan' },
  { value: 5, label: t('employeeTracking.months.may') || 'Mayıs' },
  { value: 6, label: t('employeeTracking.months.june') || 'Haziran' },
  { value: 7, label: t('employeeTracking.months.july') || 'Temmuz' },
  { value: 8, label: t('employeeTracking.months.august') || 'Ağustos' },
  { value: 9, label: t('employeeTracking.months.september') || 'Eylül' },
  { value: 10, label: t('employeeTracking.months.october') || 'Ekim' },
  { value: 11, label: t('employeeTracking.months.november') || 'Kasım' },
  { value: 12, label: t('employeeTracking.months.december') || 'Aralık' }
];

export default function EmployeeTracking() {
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const { depos: dbDepos } = useDepos();
  
  // Get translated status config and months
  const STATUS_CONFIG = useMemo(() => getStatusConfig(t), [t]);
  const MONTHS = useMemo(() => getMonthOptions(t), [t]);
  
  const [loading, setLoading] = useState(true);
  const [firms, setFirms] = useState([]);
  const [selectedFirm, setSelectedFirm] = useState('all');
  const [selectedDepo, setSelectedDepo] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [employees, setEmployees] = useState([]);
  const [statusData, setStatusData] = useState({});
  const [summary, setSummary] = useState({});
  
  // Employee filter states
  const [employeeFilter, setEmployeeFilter] = useState([]);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');

  // Generate year options
  const years = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear - 2; y <= currentYear + 1; y++) {
    years.push(y);
  }

  // Generate days of month
  const getDaysInMonth = (month, year) => {
    return new Date(year, month, 0).getDate();
  };

  const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  // Fetch firms on mount
  useEffect(() => {
    fetchFirms();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch data when firm/month/year changes
  useEffect(() => {
    fetchEmployeesAndStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFirm, selectedDepo, selectedMonth, selectedYear]);

  const fetchFirms = async () => {
    try {
      const response = await api.get('/admin/firms');
      const firmList = response.data.data || response.data || [];
      setFirms(firmList);
      
      // Auto-select firm based on user role
      if (user?.role === 'FIRM_ADMIN' && user?.firm_id) {
        setSelectedFirm(user.firm_id);
      } else if (user?.role === 'FULL_ADMIN') {
        const allowed = user?.permissions?.allowed_firms || [];
        if (allowed.length === 1) {
          setSelectedFirm(allowed[0]);
        }
      }
      // For SUPER_ADMIN, keep default 'all'
    } catch (error) {
      console.error('Error fetching firms:', error);
      toast.error(t('employeeTracking.firmsLoadError') || 'Firmalar yüklenemedi');
    }
  };

  const fetchEmployeesAndStatus = async () => {
    setLoading(true);
    try {
      // Build query params based on selected firm
      const firmParam = selectedFirm !== 'all' ? `&firm_id=${selectedFirm}` : '';
      
      // Fetch employees (all or for selected firm)
      const usersResponse = await api.get(`/admin/users?status=ACTIVE${firmParam}`);
      const userList = (usersResponse.data.data || []).filter(u => u.role !== 'SUPER_ADMIN');
      // Apply depo filter client-side
      const filteredByDepo = selectedDepo === 'all' ? userList
        : selectedDepo === '__none__' ? userList.filter(u => !u.depo)
        : userList.filter(u => u.depo === selectedDepo);
      setEmployees(filteredByDepo);

      // Fetch status data for the month
      const statusResponse = await api.get(`/daily-status/monthly?month=${selectedMonth}&year=${selectedYear}${firmParam}`);
      const statusList = statusResponse.data.data || [];
      
      // Convert to map for easy lookup
      const statusMap = {};
      const summaryMap = {};
      
      userList.forEach(emp => {
        statusMap[emp.id] = {};
        summaryMap[emp.id] = { working: 0, leave: 0, sick: 0, dop: 0, other: 0 };
      });
      
      statusList.forEach(status => {
        const userId = status.user_id;
        const day = new Date(status.date).getDate();
        
        if (statusMap[userId]) {
          statusMap[userId][day] = status.status;
          
          // Count for summary
          if (summaryMap[userId]) {
            switch(status.status) {
              case 'WORKING':
              case 'HALF_DAY':
                summaryMap[userId].working++;
                break;
              case 'ON_LEAVE':
                summaryMap[userId].leave++;
                break;
              case 'SICK':
              case 'HALF_SICK':
                summaryMap[userId].sick++;
                break;
              case 'DOP':
                summaryMap[userId].dop++;
                break;
              default:
                summaryMap[userId].other++;
            }
          }
        }
      });
      
      setStatusData(statusMap);
      setSummary(summaryMap);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('employeeTracking.dataLoadError') || 'Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handlePrevMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const getStatusCell = (userId, day) => {
    const status = statusData[userId]?.[day];
    if (!status) return null;
    
    const config = STATUS_CONFIG[status];
    if (!config) return null;
    
    return (
      <div 
        className={`w-8 h-8 flex items-center justify-center rounded text-xs font-bold ${config.color} ${config.textColor}`}
        title={config.label}
      >
        {config.code}
      </div>
    );
  };

  const getDayOfWeek = (day) => {
    const date = new Date(selectedYear, selectedMonth - 1, day);
    const dayNames = [
      t('employeeTracking.days.sun') || 'Paz',
      t('employeeTracking.days.mon') || 'Pzt',
      t('employeeTracking.days.tue') || 'Sal',
      t('employeeTracking.days.wed') || 'Çar',
      t('employeeTracking.days.thu') || 'Per',
      t('employeeTracking.days.fri') || 'Cum',
      t('employeeTracking.days.sat') || 'Cmt'
    ];
    return dayNames[date.getDay()];
  };

  const isWeekend = (day) => {
    const date = new Date(selectedYear, selectedMonth - 1, day);
    return date.getDay() === 0 || date.getDay() === 6;
  };

  const getSelectedFirmName = () => {
    if (selectedFirm === 'all') return t('employeeTracking.all') || 'Tümü';
    const firm = firms.find(f => f.id === selectedFirm);
    return firm?.name || t('employeeTracking.selectFirm') || 'Firma seçin';
  };

  // Filtered employees based on firm and employee filter
  const filteredEmployees = useMemo(() => {
    let result = employees;
    
    // Apply employee filter if set
    if (employeeFilter.length > 0) {
      result = result.filter(emp => employeeFilter.includes(emp.id));
    }
    
    return result;
  }, [employees, employeeFilter]);

  // Employees for filter dropdown (search filtered)
  const searchedEmployeesForFilter = useMemo(() => {
    if (!employeeSearchTerm) {
      return employees;
    }
    return employees.filter(emp => 
      turkishIncludes(emp.full_name || '', employeeSearchTerm) ||
      turkishIncludes(emp.email || '', employeeSearchTerm)
    );
  }, [employees, employeeSearchTerm]);

  // Calculate summary from filtered employees
  const filteredSummary = useMemo(() => {
    return {
      working: filteredEmployees.reduce((acc, emp) => acc + (summary[emp.id]?.working || 0), 0),
      leave: filteredEmployees.reduce((acc, emp) => acc + (summary[emp.id]?.leave || 0), 0),
      sick: filteredEmployees.reduce((acc, emp) => acc + (summary[emp.id]?.sick || 0), 0),
      dop: filteredEmployees.reduce((acc, emp) => acc + (summary[emp.id]?.dop || 0), 0)
    };
  }, [filteredEmployees, summary]);

  return (
    <div className="space-y-6" data-testid="employee-tracking-page">
      {/* Header */}
      <div>
        <p className="text-slate-500 dark:text-slate-400 text-lg">
          {t('employeeTracking.description') || 'Çalışanların günlük durumlarını takip edin'}
        </p>
      </div>

      {/* Filters - Single Row Layout */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Month/Year Navigation */}
        <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
          <Button variant="ghost" size="icon" className="h-9 w-9" onClick={handlePrevMonth}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          
          <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(Number(v))}>
            <SelectTrigger className="w-[100px] border-0 bg-transparent">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONTHS.map(m => (
                <SelectItem key={m.value} value={String(m.value)}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
            <SelectTrigger className="w-[90px] border-0 bg-transparent">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(y => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button variant="ghost" size="icon" className="h-9 w-9" onClick={handleNextMonth}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Firm Selector */}
        <Select 
          value={selectedFirm} 
          onValueChange={setSelectedFirm}
          disabled={user?.role === 'FIRM_ADMIN'}
        >
          <SelectTrigger className="w-[180px] h-9">
            <SelectValue placeholder={t('employeeTracking.all') || 'Tümü'}>{getSelectedFirmName()}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('employeeTracking.all') || 'Tümü'}</SelectItem>
            {firms.map(firm => (
              <SelectItem key={firm.id} value={firm.id}>
                {firm.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Depo Filter */}
        <Select value={selectedDepo} onValueChange={setSelectedDepo}>
          <SelectTrigger className="w-[150px] h-9" data-testid="tracking-depo-filter">
            <SelectValue placeholder={t('common.allDepos')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.allDepos')}</SelectItem>
            <SelectItem value="__none__">{t('common.noDepo')}</SelectItem>
            {dbDepos.map(d => (
              <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Employee Filter */}
        <Popover open={showFilterDropdown} onOpenChange={setShowFilterDropdown}>
          <PopoverTrigger asChild>
            <Button 
              variant={employeeFilter.length > 0 ? "default" : "outline"} 
              className="h-9"
            >
              <Filter className="h-4 w-4 mr-1.5" />
              {t('employeeTracking.filter') || 'Filtre'}
              {employeeFilter.length > 0 && (
                <Badge variant="secondary" className="ml-1.5 h-5 px-1.5 text-xs">
                  {employeeFilter.length}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0" align="start">
            <div className="p-3 border-b">
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-slate-400" />
                <Input
                  placeholder={t('employeeTracking.searchEmployee') || 'Çalışan ara...'}
                  className="h-8 border-0 p-0 focus-visible:ring-0"
                  value={employeeSearchTerm}
                  onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                />
                {employeeSearchTerm && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 w-6 p-0"
                    onClick={() => setEmployeeSearchTerm('')}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>
            <div className="max-h-[250px] overflow-y-auto p-2">
              {searchedEmployeesForFilter.length === 0 ? (
                <div className="text-center py-4 text-slate-500 dark:text-slate-400 text-sm">
                  {t('employeeTracking.noResults') || 'Sonuç bulunamadı'}
                </div>
              ) : (
                searchedEmployeesForFilter.map(emp => (
                  <div 
                    key={emp.id} 
                    className={cn(
                      "flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700",
                      employeeFilter.includes(emp.id) && "bg-blue-50 dark:bg-blue-900/20"
                    )}
                    onClick={() => {
                      if (employeeFilter.includes(emp.id)) {
                        setEmployeeFilter(prev => prev.filter(id => id !== emp.id));
                      } else {
                        setEmployeeFilter(prev => [...prev, emp.id]);
                      }
                    }}
                  >
                    <Checkbox checked={employeeFilter.includes(emp.id)} />
                    <span className="text-sm truncate flex-1">{emp.full_name}</span>
                    {emp.role === 'FIRM_ADMIN' && (
                      <Badge variant="outline" className="text-[10px] px-1">Admin</Badge>
                    )}
                  </div>
                ))
              )}
            </div>
            {employeeFilter.length > 0 && (
              <div className="p-2 border-t">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => setEmployeeFilter([])}
                >
                  <X className="h-4 w-4 mr-1" />
                  {t('employeeTracking.clearFilter') || 'Filtreyi Temizle'}
                </Button>
              </div>
            )}
          </PopoverContent>
        </Popover>

        {/* Refresh Button */}
        <Button variant="outline" size="icon" className="h-9 w-9" onClick={fetchEmployeesAndStatus} disabled={loading}>
          <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
        </Button>

        {/* Employee Count */}
        <Badge variant="secondary" className="ml-auto h-9 px-3">
          <Users className="h-3.5 w-3.5 mr-1.5" />
          {filteredEmployees.length} {t('employeeTracking.employees') || 'Çalışan'}
        </Badge>
      </div>

      {/* Summary Cards - moved above table */}
      {!loading && filteredEmployees.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-600">
                    {filteredSummary.working}
                  </p>
                  <p className="text-xs text-slate-500">{t('employeeTracking.totalWorking') || 'Toplam Çalışma'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900 flex items-center justify-center">
                  <Briefcase className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-orange-600">
                    {filteredSummary.leave}
                  </p>
                  <p className="text-xs text-slate-500">{t('employeeTracking.totalLeave') || 'Toplam İzin'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900 flex items-center justify-center">
                  <Heart className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">
                    {filteredSummary.sick}
                  </p>
                  <p className="text-xs text-slate-500">{t('employeeTracking.totalSick') || 'Toplam Hasta'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                  <Coffee className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-600">
                    {filteredSummary.dop}
                  </p>
                  <p className="text-xs text-slate-500">{t('employeeTracking.totalDop') || 'Toplam DOP'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Date Header and Legend */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">
            {MONTHS.find(m => m.value === selectedMonth)?.label} {selectedYear}
          </h2>
          <p className="text-sm text-slate-500">({t('employeeTracking.daySummary') || 'Gün Özeti'})</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(STATUS_CONFIG).slice(0, 7).map(([key, config]) => (
            <div key={key} className="flex items-center gap-1 text-xs">
              <div className={`w-4 h-4 rounded flex items-center justify-center ${config.color} ${config.textColor} text-[9px] font-bold`}>
                {config.code}
              </div>
              <span className="text-slate-600 dark:text-slate-400">{config.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Data Table */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Users className="h-12 w-12 mx-auto mb-4" />
              <p>{employeeFilter.length > 0 
                ? (t('employeeTracking.noFilterMatch') || 'Seçilen filtre kriterlerine uygun çalışan bulunamadı') 
                : (t('employeeTracking.noEmployeeFound') || 'Aktif çalışan bulunmuyor')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-2 font-medium sticky left-0 bg-white dark:bg-slate-950 min-w-[180px]">
                      {t('employeeTracking.employee') || 'Çalışan'}
                    </th>
                    {days.map(day => (
                      <th 
                        key={day} 
                        className={`text-center py-2 px-1 font-medium min-w-[40px] ${isWeekend(day) ? 'bg-slate-100 dark:bg-slate-800' : ''}`}
                      >
                        <div className="text-xs text-slate-400">{getDayOfWeek(day)}</div>
                        <div>{day}</div>
                      </th>
                    ))}
                    <th className="text-center py-3 px-2 font-medium bg-slate-50 dark:bg-slate-800 min-w-[60px]">Ç</th>
                    <th className="text-center py-3 px-2 font-medium bg-slate-50 dark:bg-slate-800 min-w-[60px]">İ</th>
                    <th className="text-center py-3 px-2 font-medium bg-slate-50 dark:bg-slate-800 min-w-[60px]">H</th>
                    <th className="text-center py-3 px-2 font-medium bg-slate-50 dark:bg-slate-800 min-w-[60px]">D</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEmployees.map(emp => (
                    <tr key={emp.id} className="border-b hover:bg-slate-50 dark:hover:bg-slate-900">
                      <td className="py-2 px-2 sticky left-0 bg-white dark:bg-slate-950">
                        <div className="font-medium truncate max-w-[160px]" title={emp.full_name}>
                          {emp.full_name}
                        </div>
                        <div className="text-xs text-slate-400 truncate max-w-[160px]">
                          {emp.email}
                        </div>
                      </td>
                      {days.map(day => (
                        <td 
                          key={day} 
                          className={`text-center py-2 px-1 ${isWeekend(day) ? 'bg-slate-50 dark:bg-slate-800/50' : ''}`}
                        >
                          {getStatusCell(emp.id, day)}
                        </td>
                      ))}
                      {/* Summary columns */}
                      <td className="text-center py-2 px-2 bg-emerald-50 dark:bg-emerald-900/20 font-medium text-emerald-700 dark:text-emerald-400">
                        {summary[emp.id]?.working || 0}
                      </td>
                      <td className="text-center py-2 px-2 bg-orange-50 dark:bg-orange-900/20 font-medium text-orange-700 dark:text-orange-400">
                        {summary[emp.id]?.leave || 0}
                      </td>
                      <td className="text-center py-2 px-2 bg-red-50 dark:bg-red-900/20 font-medium text-red-700 dark:text-red-400">
                        {summary[emp.id]?.sick || 0}
                      </td>
                      <td className="text-center py-2 px-2 bg-blue-50 dark:bg-blue-900/20 font-medium text-blue-700 dark:text-blue-400">
                        {summary[emp.id]?.dop || 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
